import type { ElementType, PropsWithChildren } from 'react'
import {
  type StyleProp,
  type TextStyle,
  TouchableOpacity,
  View,
  Text,
  StyleSheet,
} from 'react-native'
import { Check } from '@/src/assets/icons/Check'
import { ClockIcon } from '@/src/assets/icons/ClockIcon'
import { mesaColorNames, fontFamilies } from '@/src/common/theme'
import { RefreshIcon } from '@/src/assets/icons/RefreshIcon'

export function OptionItemSelector({
  label,
  title,
  isSelected = false,
  onItemPress,
  withBorder = true,
  titleStyle,
  subtitle,
  isPending = false,
  testID = '',
  hideIcon,
  icon,
  disabled = false,
  isUpdateRequired = false,
}: {
  label?: string
  title?: PropsWithChildren['children']
  subtitle?: string
  isSelected?: boolean
  onItemPress?: () => void
  withBorder?: boolean
  titleStyle?: StyleProp<TextStyle>
  isPending?: boolean
  testID?: string
  hideIcon?: boolean
  icon?: PropsWithChildren['children']
  disabled?: boolean
  isUpdateRequired?: boolean
}) {
  const Container: ElementType = onItemPress ? TouchableOpacity : View

  return (
    <Container
      testID={testID}
      onPress={disabled ? undefined : onItemPress}
      style={[
        styles.container,
        { borderBottomWidth: withBorder ? 1 : 0 },
        isPending && styles.pendingContainer,
        disabled && styles.disabled,
      ]}
    >
      {!hideIcon && (
        <View style={styles.iconContainer}>
          {icon ||
            (isSelected ? (
              <Check color={mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9']} />
            ) : (
              <View style={styles.placeholderIcon} />
            ))}
        </View>
      )}
      {isPending && (
        <View style={styles.iconContainer}>
          <ClockIcon />
        </View>
      )}
      {isUpdateRequired && (
        <View style={styles.iconContainer}>
          <RefreshIcon />
        </View>
      )}
      <View style={styles.content}>
        {Boolean(label) && <Text style={styles.label}>{label}</Text>}
        {typeof title === 'string' ? (
          <Text style={[styles.title, titleStyle]}>{title}</Text>
        ) : (
          title
        )}
        {Boolean(subtitle) && <Text style={styles.label}>{subtitle}</Text>}
      </View>
    </Container>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    borderBottomColor: mesaColorNames['semantic/bg/disabled #DADDDE'],
    paddingVertical: 10,
  },
  pendingContainer: {
    opacity: 0.5,
  },
  iconContainer: {
    height: 25,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: 8,
    marginHorizontal: 4,
  },
  placeholderIcon: {
    width: 25,
    marginVertical: 8,
  },
  content: {
    flex: 1,
    flexDirection: 'column',
    gap: 3,
    justifyContent: 'center',
    alignContent: 'center',
  },
  label: {
    color: mesaColorNames['global/grey/60 #6E7375'],
    fontSize: 14,
  },
  title: {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    color: mesaColorNames['global/grey/80 #2F3133'],
  },
  disabled: {
    opacity: 0.5,
  },
})

export default OptionItemSelector
